import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests when available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Course {
  id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  industryCategory: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  estimatedWeeks: number;
  price: number;
  instructorId: string;
  instructorName: string;
  instructorBio?: string;
  thumbnailUrl?: string;
  videoPreviewUrl?: string;
  status: 'draft' | 'published' | 'archived';
  tags: string[];
  skills: string[];
  prerequisites: string[];
  learningObjectives: string[];
  template: string;
  marketData: {
    jobDemand: number;
    averageSalary: number;
    salaryRange: { min: number; max: number };
    careerPaths: string[];
    relatedSkills: string[];
    marketTrend: 'rising' | 'stable' | 'declining';
  };
  enrollmentCount: number;
  /** Set by the API when the count was withheld (signed-out viewer). */
  enrollmentCountLocked?: boolean;
  completionRate: number;
  averageRating: number;
  reviewCount: number;
  createdBy: 'platform' | string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  lastModified: string;
  version: string;
}

export interface IndustryCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  averageSalary: {
    entry: number;
    mid: number;
    senior: number;
  };
  jobDemand: number;
  growthRate: number;
  popularSkills: string[];
}

export interface CourseTemplate {
  id: string;
  name: string;
  description: string;
  structure: {
    modulesBeforeMidterm: number;
    hasMidterm: boolean;
    modulesAfterMidterm: number;
    hasFinal: boolean;
    totalModules: number;
    assessmentWeights: {
      quizzes: number;
      assignments: number;
      midterm?: number;
      final?: number;
    };
  };
}

export interface CourseFilters {
  category?: string;
  industryCategory?: string;
  difficulty?: string;
  minDuration?: number;
  maxDuration?: number;
  minSalary?: number;
  searchQuery?: string;
  sortBy?: 'popular' | 'rating' | 'demand' | 'salary' | 'duration';
  limit?: number;
}

export interface CourseStats {
  totalCourses: number;
  categories: string[];
  industryCategories: string[];
  difficulties: string[];
  durationRange: { min: number; max: number };
  salaryRange: { min: number; max: number };
  popularSkills: Record<string, number>;
}

class CourseService {
  // Get all courses with optional filtering
  async getCourses(filters?: CourseFilters): Promise<{ courses: Course[]; total: number }> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      
      const response = await api.get(`/?${params.toString()}`);
      return {
        courses: response.data.data,
        total: response.data.total
      };
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw new Error('Failed to fetch courses');
    }
  }

  // Get course by ID
  async getCourseById(courseId: string): Promise<Course> {
    try {
      const response = await api.get(`/${courseId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching course:', error);
      throw new Error('Failed to fetch course');
    }
  }

  // Get industry categories
  async getIndustryCategories(): Promise<IndustryCategory[]> {
    try {
      const response = await api.get('/meta/categories');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching industry categories:', error);
      throw new Error('Failed to fetch industry categories');
    }
  }

  // Get course templates
  async getCourseTemplates(): Promise<CourseTemplate[]> {
    try {
      const response = await api.get('/meta/templates');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching course templates:', error);
      throw new Error('Failed to fetch course templates');
    }
  }

  // Get course statistics for filters
  async getCourseStats(): Promise<CourseStats> {
    try {
      const response = await api.get('/meta/stats');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching course stats:', error);
      throw new Error('Failed to fetch course statistics');
    }
  }

  // Enroll in course (requires authentication)
  async enrollInCourse(courseId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/${courseId}/enroll`);
      return response.data;
    } catch (error) {
      console.error('Error enrolling in course:', error);
      throw new Error('Failed to enroll in course');
    }
  }

  // Get user's enrolled courses (requires authentication)
  async getEnrolledCourses(): Promise<Course[]> {
    try {
      const response = await api.get('/my/enrolled');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      throw new Error('Failed to fetch enrolled courses');
    }
  }

  // Submit course review (requires authentication)
  async submitCourseReview(courseId: string, rating: number, review: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/${courseId}/review`, {
        rating,
        review
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting course review:', error);
      throw new Error('Failed to submit course review');
    }
  }

  // Search courses
  async searchCourses(query: string): Promise<Course[]> {
    try {
      const { courses } = await this.getCourses({ searchQuery: query });
      return courses;
    } catch (error) {
      console.error('Error searching courses:', error);
      throw new Error('Failed to search courses');
    }
  }

  // Get popular courses
  async getPopularCourses(limit: number = 6): Promise<Course[]> {
    try {
      const { courses } = await this.getCourses({ sortBy: 'popular', limit });
      return courses;
    } catch (error) {
      console.error('Error fetching popular courses:', error);
      throw new Error('Failed to fetch popular courses');
    }
  }

  // Get trending courses (based on job demand)
  async getTrendingCourses(limit: number = 6): Promise<Course[]> {
    try {
      const { courses } = await this.getCourses({ sortBy: 'demand', limit });
      return courses;
    } catch (error) {
      console.error('Error fetching trending courses:', error);
      throw new Error('Failed to fetch trending courses');
    }
  }
}

const courseServiceInstance = new CourseService();
export default courseServiceInstance;